'use client';

import { useMutation } from '@tanstack/react-query';
import { PencilIcon, Triangle } from 'lucide-react';

import React, { useCallback, useState } from 'react';

import { Button } from '@/components/ui/button';

import { ImageUploadDialog, toast } from '@/shared/components';
import { uploadTempImage } from '@/shared/services/forms/forms';

import { cn } from '@/lib/utils';

interface Props {
	className?: string;
	logoUrl?: string;
	onLogoUpdate?: (logoUrl: string) => void;
	editMode?: boolean;
	compactSpacing?: boolean;
}

function CompanyLogo({
	className = '',
	logoUrl,
	onLogoUpdate,
	editMode = false,
	compactSpacing = false,
}: Props) {
	const [imageUploadDialogOpen, setImageUploadDialogOpen] = useState(false);
	const [companyLogo, setCompanyLogo] = useState<string | null>(logoUrl ?? null);

	const { mutateAsync: uploadLogoAsync } = useMutation({
		mutationFn: uploadTempImage,
		onSuccess: (data) => {
			if (data.imageUrl) {
				setCompanyLogo(data.imageUrl);
				onLogoUpdate?.(data.imageUrl);
				toast({
					title: 'Logo uploaded successfully',
					variant: 'success',
				});
			}
		},
		onError: () => {
			toast({
				title: 'Failed to upload logo',
				description: 'Please try again later',
				variant: 'destructive',
			});
		},
	});

	const handleImageUpload = useCallback(
		async (file: File) => {
			const formData = new FormData();
			formData.append('image', file);
			await uploadLogoAsync(formData);
		},
		[uploadLogoAsync]
	);

	const handleRemoveLogo = useCallback(async () => {
		setCompanyLogo(null);
		onLogoUpdate?.('');
		toast({
			title: 'Logo removed successfully',
			variant: 'success',
		});
	}, [onLogoUpdate]);

	return (
		<div className={cn('flex w-fit flex-col', className)}>
			{companyLogo ? (
				<div
					className={cn('group relative flex w-fit items-center', compactSpacing ? 'mb-2' : 'mb-4')}
				>
					<img
						src={companyLogo}
						alt="Company logo"
						className={cn('size-20 rounded-sm')}
					/>
					{editMode && (
						<div className="absolute -right-1 -top-1 cursor-pointer rounded-sm border bg-background p-1 opacity-0 transition-opacity group-hover:opacity-100">
							<PencilIcon
								className="size-4 text-muted-foreground"
								onClick={() => setImageUploadDialogOpen(true)}
							/>
						</div>
					)}
				</div>
			) : (
				<Button
					variant={'outline'}
					size={'sm'}
					onClick={() => setImageUploadDialogOpen(true)}
					className="flex h-fit w-fit items-center gap-2 rounded-sm px-2 py-1 text-[12px] text-muted-foreground"
				>
					<Triangle className="size-3" />
					Add logo
				</Button>
			)}

			<ImageUploadDialog
				open={imageUploadDialogOpen}
				onOpenChange={() => setImageUploadDialogOpen(!imageUploadDialogOpen)}
				onImageUpload={handleImageUpload}
				currentImageUrl={companyLogo || undefined}
				onImageRemove={editMode ? handleRemoveLogo : undefined}
				aspectRatio={1}
			/>
		</div>
	);
}

export default CompanyLogo;
